/** 
 * Nosto-ProductGrid.js
 *
 * Grid de productos con pestañas y botón "Ver más" / "Ver menos".
 *
 * Responsabilidades:
 * - Cambiar entre pestañas.
 * - Escuchar recomendaciones de Nosto y renderizar cada producto
 *   con la vista product.nosto-card-mini (orden de Nosto).
 * - Mostrar N filas iniciales, expandir con "Ver más" y contraer con "Ver menos".
 * - Abrir el quick buy global con el botón "+".
 */

(() => {
  "use strict";

  const CARD_VIEW = "nosto-card-mini";

  class NostoProductGrid extends HTMLElement {
    constructor() {
      super();

      this.destroyed = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleRecommendationsLoaded = this.handleRecommendationsLoaded.bind(this);
      this.handleRecommendationsError = this.handleRecommendationsError.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    /* ======================================================================
       Lifecycle
       ====================================================================== */

    connectedCallback() {
      this.destroyed = false;

      this.initialRows = this.getPositiveInt(this.dataset.initialRows, 1);
      this.lastColumns = 0;

      this.addEventListener("click", this.handleClick);
      this.addEventListener("nosto:recommendations:loaded", this.handleRecommendationsLoaded);
      this.addEventListener("nosto:recommendations:error", this.handleRecommendationsError);
      window.addEventListener("resize", this.handleResize);

      this.initializeQuickBuyModal();

      /*
       * Las recomendaciones pudieron cargarse antes de que este
       * custom element se definiera (script en la sección).
       */
      this.querySelectorAll("[data-nosto-grid-recommendations]").forEach((recommendations) => {
        if (recommendations.hasLoaded && recommendations.lastResult) {
          this.storeRecommendations(recommendations, recommendations.lastResult);
        }
      });

      this.querySelectorAll("[data-nosto-grid-panel]").forEach((panel) => {
        if (panel.dataset.sourceType === "collection_tab") {
          if (!panel.querySelector("[data-nosto-grid-item]")) {
            this.setPanelEmpty(panel);
          }

          panel.dataset.rendered = "true";
        }

        /*
         * Tab de Nosto sin Placement ID: no hay nada que cargar.
         */
        if (
          panel.dataset.sourceType === "nosto_tab" &&
          !panel.querySelector("[data-nosto-grid-recommendations]")
        ) {
          panel.querySelectorAll("[data-nosto-grid-skeleton]").forEach((skeleton) => skeleton.remove());
          this.setPanelEmpty(panel);
          panel.dataset.rendered = "true";
        }
      });

      const activePanel = this.getActivePanel();

      if (activePanel) {
        this.initializePanel(activePanel);
      }
    }

    disconnectedCallback() {
      this.destroyed = true;

      this.removeEventListener("click", this.handleClick);
      this.removeEventListener("nosto:recommendations:loaded", this.handleRecommendationsLoaded);
      this.removeEventListener("nosto:recommendations:error", this.handleRecommendationsError);
      window.removeEventListener("resize", this.handleResize);
    }

    /* ======================================================================
       Services
       ====================================================================== */

    getProductViewService() {
      if (this.productViewService) {
        return this.productViewService;
      }

      if (window.NOSTO_PRODUCT_VIEW_SERVICE) {
        this.productViewService = window.NOSTO_PRODUCT_VIEW_SERVICE;
      } else if (typeof window.NostoProductViewService === "function") {
        this.productViewService = new window.NostoProductViewService({
          cardView: "product-card",
          quickBuyView: "nosto-quick-buy",
          concurrency: 4,
          cache: true,
          debug: window.NOSTO_DEBUG === true,
        });

        window.NOSTO_PRODUCT_VIEW_SERVICE = this.productViewService;
      }

      return this.productViewService || null;
    }

    initializeQuickBuyModal() {
      if (window.NOSTO_QUICK_BUY_MODAL?.isInitialized) {
        return;
      }

      const modalElement = document.querySelector("[data-nosto-quick-buy-modal]");
      const viewService = this.getProductViewService();

      if (typeof window.NostoQuickBuyModal !== "function" || !modalElement || !viewService) {
        return;
      }

      try {
        const controller = new window.NostoQuickBuyModal({
          modal: modalElement,
          viewService,
          debug: window.NOSTO_DEBUG === true,
        });

        controller.init();

        window.NOSTO_QUICK_BUY_MODAL = controller;
      } catch (error) {
        console.error("[NostoProductGrid] No fue posible inicializar el quick buy.", error);
      }
    }

    /* ======================================================================
       Clicks
       ====================================================================== */

    handleClick(event) {
      const tab = event.target.closest("[data-nosto-grid-tab]");

      if (tab && this.contains(tab)) {
        event.preventDefault();
        this.activateTab(tab);
        return;
      }

      const moreButton = event.target.closest("[data-nosto-grid-more-button]");

      if (moreButton && this.contains(moreButton)) {
        event.preventDefault();

        const panel = moreButton.closest("[data-nosto-grid-panel]");

        if (panel) {
          const expand = panel.dataset.expanded !== "true";

          panel.dataset.expanded = String(expand);
          this.updateVisibility(panel);

          /*
           * Al contraer, si el inicio del grid quedó fuera de la vista,
           * regresamos a él.
           */
          if (!expand) {
            const top = panel.getBoundingClientRect().top;

            if (top < 0) {
              panel.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }
        }

        return;
      }

      const quickAdd = event.target.closest("[data-nosto-card-mini-quick-add]");

      if (quickAdd && this.contains(quickAdd)) {
        event.preventDefault();
        this.openQuickBuy(quickAdd);
      }
    }

    openQuickBuy(button) {
      if (button.disabled) {
        return;
      }

      this.initializeQuickBuyModal();

      const item = button.closest("[data-nosto-grid-item]");
      const attributionKey = item?.dataset.nostoResultId || "";

      document.dispatchEvent(
        new CustomEvent("nosto:quick-buy:open", {
          bubbles: true,
          detail: {
            productId: button.dataset.productId || "",
            productHandle: button.dataset.productHandle || "",
            productUrl: button.dataset.productUrl || "",
            quickBuyUrl: button.dataset.quickBuyUrl || "",
            view: "nosto-quick-buy",
            button,
            source: attributionKey ? "grid-recommendation" : "grid-collection",
            track: attributionKey ? "recommendation" : "",
            attributionKey,
            context: {
              placementId: item?.dataset.nostoPlacementId || "",
              resultId: attributionKey,
            },
          },
        })
      );
    }

    /* ======================================================================
       Tabs
       ====================================================================== */

    getActivePanel() {
      return this.querySelector("[data-nosto-grid-panel].is-active");
    }

    activateTab(selectedTab) {
      const panelId = selectedTab.dataset.panelId;

      if (!panelId) {
        return;
      }

      this.querySelectorAll("[data-nosto-grid-tab]").forEach((tab) => {
        const isSelected = tab === selectedTab;

        tab.classList.toggle("is-active", isSelected);
        tab.setAttribute("aria-selected", String(isSelected));
      });

      let activePanel = null;

      this.querySelectorAll("[data-nosto-grid-panel]").forEach((panel) => {
        const isSelected = panel.id === panelId;

        panel.hidden = !isSelected;
        panel.classList.toggle("is-active", isSelected);

        if (isSelected) {
          activePanel = panel;
        }
      });

      if (activePanel) {
        this.initializePanel(activePanel);
      }
    }

    async initializePanel(panel) {
      if (
        panel.dataset.sourceType === "nosto_tab" &&
        panel.dataset.rendered !== "true" &&
        Array.isArray(panel.nostoRecommendedProducts)
      ) {
        await this.renderNostoPanel(panel);
      }

      this.updateVisibility(panel);
    }

    /* ======================================================================
       Nosto recommendations
       ====================================================================== */

    handleRecommendationsLoaded(event) {
      const recommendations = event.target.closest("nosto-recommendations");

      if (!recommendations || !event.detail) {
        return;
      }

      const panel = this.storeRecommendations(recommendations, event.detail);

      if (panel && panel.classList.contains("is-active")) {
        this.initializePanel(panel);
      }
    }

    handleRecommendationsError(event) {
      const panel = event.target.closest("[data-nosto-grid-panel]");

      if (!panel || !this.contains(panel)) {
        return;
      }

      this.setPanelEmpty(panel);

      const status = panel.querySelector("[data-nosto-grid-status]");

      if (status) {
        status.textContent = "No fue posible cargar las recomendaciones.";
      }
    }

    storeRecommendations(recommendations, detail) {
      const panel = recommendations.closest("[data-nosto-grid-panel]");

      if (!panel || !this.contains(panel) || panel.dataset.recommendationsReady === "true") {
        return null;
      }

      let products = this.getRecommendedProducts(detail);

      const limit = Number.parseInt(recommendations.dataset.limit, 10);

      if (Number.isFinite(limit) && limit > 0) {
        products = products.slice(0, limit);
      }

      panel.nostoRecommendationContext = {
        placementId: String(detail.placementId || "").trim(),
        resultId: String(detail.resultId || "").trim(),
        divId: String(detail.divId || "").trim(),
      };

      panel.nostoRecommendedProducts = products;
      panel.dataset.recommendationsReady = "true";

      return panel;
    }

    getRecommendedProducts(detail) {
      const source =
        detail.allRecommendedProducts ??
        detail.recommendedProducts ??
        detail.products ??
        [];

      if (!Array.isArray(source)) {
        return [];
      }

      const seen = new Set();
      const products = [];

      source.forEach((product) => {
        const normalized = this.normalizeProduct(product);

        if (!normalized) {
          return;
        }

        const key = normalized.handle || normalized.productId;

        if (!key || seen.has(key)) {
          return;
        }

        seen.add(key);
        products.push(normalized);
      });

      return products;
    }

    normalizeProduct(product) {
      if (!product) {
        return null;
      }

      if (typeof product === "string") {
        const value = product.trim();

        return value ? { productId: value, handle: "", url: "" } : null;
      }

      const productId = this.normalizeString(product.productId ?? product.product_id ?? product.id);
      const url = this.normalizeString(product.url ?? product.productUrl ?? product.product_url);
      const handle =
        this.normalizeString(product.handle ?? product.productHandle ?? product.product_handle) ||
        this.getHandleFromUrl(url);

      if (!productId && !handle) {
        return null;
      }

      return { productId, handle, url };
    }

    getHandleFromUrl(url) {
      if (!url) {
        return "";
      }

      try {
        const match = new URL(url, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);

        return match?.[1] || "";
      } catch (error) {
        return "";
      }
    }

    async renderNostoPanel(panel) {
      if (panel.dataset.rendering === "true") {
        return;
      }

      const items = panel.querySelector("[data-nosto-grid-items]");
      const products = panel.nostoRecommendedProducts || [];
      const viewService = this.getProductViewService();

      if (!items) {
        return;
      }

      if (!products.length || !viewService) {
        items.querySelectorAll("[data-nosto-grid-skeleton]").forEach((skeleton) => skeleton.remove());
        this.setPanelEmpty(panel);
        panel.dataset.rendered = "true";
        return;
      }

      panel.dataset.rendering = "true";

      const context = panel.nostoRecommendationContext || {};

      try {
        const results = await Promise.all(
          products.map((product) =>
            viewService
              .getCard({
                handle: product.handle || "",
                url: product.url || "",
                productId: product.productId || "",
                view: CARD_VIEW,
                context: {
                  source: "grid-recommendation",
                  track: "recommendation",
                  attributionKey: context.resultId || "",
                  placementId: context.placementId || "",
                  resultId: context.resultId || "",
                  divId: context.divId || "",
                },
              })
              .catch((error) => {
                console.error("[NostoProductGrid] No se pudo renderizar el producto.", { product, error });
                return "";
              })
          )
        );

        if (this.destroyed) {
          return;
        }

        const fragment = document.createDocumentFragment();

        results.forEach((html) => {
          if (!html) {
            return;
          }

          const template = document.createElement("template");
          template.innerHTML = String(html).trim();

          const card = template.content.querySelector("[data-nosto-product-card-mini]");

          if (!card) {
            return;
          }

          this.applyAttribution(card, context.resultId);

          const item = document.createElement("div");
          item.className = "nosto-product-grid__item";
          item.dataset.nostoGridItem = "";
          item.dataset.productSource = "nosto-recommendation";
          item.dataset.nostoPlacementId = context.placementId || "";
          item.dataset.nostoResultId = context.resultId || "";
          item.dataset.nostoDivId = context.divId || "";
          item.setAttribute("role", "listitem");
          item.appendChild(card);

          fragment.appendChild(item);
        });

        items.replaceChildren(fragment);

        if (!items.children.length) {
          this.setPanelEmpty(panel);
        }

        panel.dataset.rendered = "true";
        delete panel.dataset.expanded;
      } finally {
        delete panel.dataset.rendering;
      }
    }

    /*
     * Agrega ?nosto=<resultId> a los enlaces de la card para que la
     * visita al producto registre la attribution (Nosto-VPAttribution.js).
     */
    applyAttribution(card, resultId) {
      if (!resultId) {
        return;
      }

      card.querySelectorAll("a[href]").forEach((link) => {
        const url = new URL(link.getAttribute("href"), window.location.origin);

        url.searchParams.set("nosto", resultId);

        link.setAttribute("href", `${url.pathname}${url.search}${url.hash}`);
      });
    }

    setPanelEmpty(panel) {
      panel.dataset.empty = "true";

      const more = panel.querySelector("[data-nosto-grid-more]");

      if (more) {
        more.hidden = true;
      }
    }

    /* ======================================================================
       Ver más / Ver menos
       ====================================================================== */

    getColumns(panel) {
      const items = panel.querySelector("[data-nosto-grid-items]");

      if (!items) {
        return 1;
      }

      const template = window.getComputedStyle(items).gridTemplateColumns;
      const columns = template && template !== "none" ? template.trim().split(/\s+/).length : 1;

      return Math.max(1, columns);
    }

    updateVisibility(panel) {
      if (!panel || panel.hidden) {
        return;
      }

      const items = Array.from(panel.querySelectorAll("[data-nosto-grid-item]"));
      const more = panel.querySelector("[data-nosto-grid-more]");
      const columns = this.getColumns(panel);

      this.lastColumns = columns;

      /*
       * Los skeletons permanecen visibles mientras carga Nosto.
       */
      if (!items.length) {
        if (more) {
          more.hidden = true;
        }

        return;
      }

      const initialCount = columns * this.initialRows;
      const canToggle = items.length > initialCount;
      const isExpanded = canToggle && panel.dataset.expanded === "true";
      const visibleCount = isExpanded ? items.length : initialCount;
      const hasMore = items.length > visibleCount;
      const peekEnd = visibleCount + columns;

      items.forEach((item, index) => {
        const isVisible = index < visibleCount;
        const isPeek = hasMore && !isVisible && index < peekEnd;

        item.classList.toggle("is-hidden", !isVisible && !isPeek);
        item.classList.toggle("is-peek", isPeek);

        if (isPeek) {
          item.setAttribute("aria-hidden", "true");
          item.setAttribute("inert", "");
        } else {
          item.removeAttribute("aria-hidden");
          item.removeAttribute("inert");
        }
      });

      if (more) {
        more.hidden = !canToggle;
        more.classList.toggle("is-expanded", isExpanded);

        const button = more.querySelector("[data-nosto-grid-more-button]");

        if (button) {
          const label = isExpanded ? button.dataset.labelLess : button.dataset.labelMore;

          if (label) {
            button.textContent = label;
          }

          button.setAttribute("aria-expanded", String(isExpanded));
        }
      }
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        const panel = this.getActivePanel();

        if (panel && this.getColumns(panel) !== this.lastColumns) {
          this.updateVisibility(panel);
        }
      }, 150);
    }

    /* ======================================================================
       Utils
       ====================================================================== */

    getPositiveInt(value, fallback) {
      const parsed = Number.parseInt(value, 10);

      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }

    normalizeString(value) {
      if (value === null || value === undefined) {
        return "";
      }

      return String(value).trim();
    }
  }

  if (!customElements.get("nosto-product-grid")) {
    customElements.define("nosto-product-grid", NostoProductGrid);
  }
})();
